/**
 * IVM Simulation Engine
 *
 * Double-buffered state channels, flat neighbor topology,
 * preset/filter orchestration for per-triangle cellular simulations.
 *
 * Frame lifecycle:
 *   preset.update(ctx) → swap buffers → filter.postUpdate(ctx) →
 *   per-triangle: preset.visual(i, ctx) → filter.transform(i, visual)
 */

import type { TriCubeCoord } from './core/types';
import { toAxial, fromAxial } from './core/tri-cube';

// === Types ===

export interface TriangleState {
  energy: Float32Array;
  phase: Float32Array;
  charge: Float32Array;
  temperature: Float32Array;
  memory: Float32Array;
  clan: Float32Array;
}

export interface IVMContext {
  count: number;
  current: TriangleState;
  next: TriangleState;
  neighbors: Int32Array;       // flat [count*3], -1 for boundary
  orientations: Uint8Array;    // 1=up, 0=down
  normalizedX: Float32Array;
  normalizedY: Float32Array;
  coordA: Float32Array;
  coordB: Float32Array;
  coordC: Float32Array;
  extra: Map<string, Float32Array>;  // preset-specific channels
  frame: number;
  dt: number;
  elapsed: number;
  mouseX: number;
  mouseY: number;
  mouseActive: boolean;
}

export interface VisualOutput {
  opacity: number;
  scale: number;
  rotation: number;
  color: string | null;        // null = use default tri.color
}

export interface IVMPreset {
  id: string;
  name: string;
  category: string;
  extraChannels?: string[];    // additional Float32Array channels needed
  init(ctx: IVMContext): void;
  update(ctx: IVMContext): void;
  visual(i: number, ctx: IVMContext): VisualOutput;
}

export interface IVMFilter {
  id: string;
  name: string;
  extraChannels?: string[];
  init?(ctx: IVMContext): void;
  postUpdate(ctx: IVMContext): void;
  transform(i: number, base: VisualOutput, ctx: IVMContext): VisualOutput;
}

// === Engine ===

function allocateState(count: number): TriangleState {
  return {
    energy: new Float32Array(count),
    phase: new Float32Array(count),
    charge: new Float32Array(count),
    temperature: new Float32Array(count),
    memory: new Float32Array(count),
    clan: new Float32Array(count),
  };
}

export class IVMEngine {
  private count = 0;
  private bufferA: TriangleState = allocateState(0);
  private bufferB: TriangleState = allocateState(0);
  private neighborIdx: Int32Array = new Int32Array(0);
  private orientations: Uint8Array = new Uint8Array(0);
  private normX: Float32Array = new Float32Array(0);
  private normY: Float32Array = new Float32Array(0);
  private cA: Float32Array = new Float32Array(0);
  private cB: Float32Array = new Float32Array(0);
  private cC: Float32Array = new Float32Array(0);
  private extra: Map<string, Float32Array> = new Map();
  private currentIsA = true;
  private frame = 0;

  private preset: IVMPreset | null = null;
  private filter: IVMFilter | null = null;

  // Cached visual outputs for render pass
  private visuals: VisualOutput[] = [];

  /**
   * Initialize engine with triangle data.
   * Called after generateGrid() produces triangles + coordIndexMap.
   */
  init(
    count: number,
    coordIndexMap: Map<string, number>,
    triangles: Array<{
      coord: TriCubeCoord;
      normalizedX: number;
      normalizedY: number;
      orientation: 'up' | 'down';
    }>,
  ): void {
    this.count = count;
    this.bufferA = allocateState(count);
    this.bufferB = allocateState(count);
    this.currentIsA = true;
    this.frame = 0;
    this.extra.clear();

    // Orientations + normalized positions + coordinates
    this.orientations = new Uint8Array(count);
    this.normX = new Float32Array(count);
    this.normY = new Float32Array(count);
    this.cA = new Float32Array(count);
    this.cB = new Float32Array(count);
    this.cC = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const t = triangles[i];
      this.orientations[i] = t.orientation === 'up' ? 1 : 0;
      this.normX[i] = t.normalizedX;
      this.normY[i] = t.normalizedY;
      this.cA[i] = t.coord.a;
      this.cB[i] = t.coord.b;
      this.cC[i] = t.coord.c;
    }

    // Build flat neighbor array using axial coords for correct screen-layout edges.
    // tri-cube neighbors() has a y-flip on the a-axis neighbor because generateGrid()
    // uses screen y-down but tri-cube assumes math y-up. Two of three neighbors are
    // same-row (correct), but the vertical one points the wrong way. So we compute
    // all three directly from axial coords:
    //   UP  at (row, col): edges at (row, col-1), (row, col+1), (row+1, col)
    //   DOWN at (row, col): edges at (row, col-1), (row, col+1), (row-1, col)
    this.neighborIdx = new Int32Array(count * 3).fill(-1);
    for (let i = 0; i < count; i++) {
      const t = triangles[i];
      const { row, col } = toAxial(t.coord);
      const isUp = t.orientation === 'up';
      const nbrAxials = [
        { row, col: col - 1 },                          // same-row left
        { row, col: col + 1 },                          // same-row right
        { row: isUp ? row + 1 : row - 1, col },         // vertical edge neighbor
      ];
      for (let j = 0; j < 3; j++) {
        const nCoord = fromAxial(nbrAxials[j].row, nbrAxials[j].col);
        const key = `${nCoord.a},${nCoord.b},${nCoord.c}`;
        const idx = coordIndexMap.get(key);
        this.neighborIdx[i * 3 + j] = idx !== undefined ? idx : -1;
      }
    }

    // Allocate visuals
    this.visuals = new Array(count);
    for (let i = 0; i < count; i++) {
      this.visuals[i] = { opacity: 0.5, scale: 1, rotation: 0, color: null };
    }

    // Initialize extra channels for preset + filter
    this.allocateExtraChannels();

    // Run preset init
    if (this.preset) {
      this.preset.init(this.buildContext(0, 0, 0.5, 0.5, false));
    }
    if (this.filter?.init) {
      this.filter.init(this.buildContext(0, 0, 0.5, 0.5, false));
    }
  }

  private allocateExtraChannels(): void {
    this.extra.clear();
    const channels = new Set<string>();
    if (this.preset?.extraChannels) {
      for (const ch of this.preset.extraChannels) channels.add(ch);
    }
    if (this.filter?.extraChannels) {
      for (const ch of this.filter.extraChannels) channels.add(ch);
    }
    for (const ch of channels) {
      this.extra.set(ch, new Float32Array(this.count));
    }
  }

  private buildContext(
    dt: number,
    elapsed: number,
    mouseX: number,
    mouseY: number,
    mouseActive: boolean,
  ): IVMContext {
    return {
      count: this.count,
      current: this.currentIsA ? this.bufferA : this.bufferB,
      next: this.currentIsA ? this.bufferB : this.bufferA,
      neighbors: this.neighborIdx,
      orientations: this.orientations,
      normalizedX: this.normX,
      normalizedY: this.normY,
      coordA: this.cA,
      coordB: this.cB,
      coordC: this.cC,
      extra: this.extra,
      frame: this.frame,
      dt,
      elapsed,
      mouseX,
      mouseY,
      mouseActive,
    };
  }

  /**
   * Set active preset. Re-initializes if engine already has triangles.
   */
  setPreset(preset: IVMPreset | null): void {
    this.preset = preset;
    if (this.count > 0 && preset) {
      this.allocateExtraChannels();
      // Zero out buffers
      const zero = (s: TriangleState) => {
        s.energy.fill(0); s.phase.fill(0); s.charge.fill(0);
        s.temperature.fill(0); s.memory.fill(0); s.clan.fill(0);
      };
      zero(this.bufferA);
      zero(this.bufferB);
      for (const arr of this.extra.values()) arr.fill(0);
      this.frame = 0;
      this.currentIsA = true;
      preset.init(this.buildContext(0, 0, 0.5, 0.5, false));
    }
  }

  /**
   * Set active filter (overlay).
   */
  setFilter(filter: IVMFilter | null): void {
    this.filter = filter;
    if (this.count > 0) {
      this.allocateExtraChannels();
      if (filter?.init) {
        filter.init(this.buildContext(0, 0, 0.5, 0.5, false));
      }
    }
  }

  /**
   * Update simulation: one frame tick.
   */
  update(dt: number, elapsed: number, mouseX: number, mouseY: number, mouseActive: boolean): void {
    if (!this.preset || this.count === 0) return;

    // 1. Copy current → next (clean slate — no stale data)
    const cur = this.currentIsA ? this.bufferA : this.bufferB;
    const nxt = this.currentIsA ? this.bufferB : this.bufferA;
    nxt.energy.set(cur.energy);
    nxt.phase.set(cur.phase);
    nxt.charge.set(cur.charge);
    nxt.temperature.set(cur.temperature);
    nxt.memory.set(cur.memory);
    nxt.clan.set(cur.clan);

    const ctx = this.buildContext(dt, elapsed, mouseX, mouseY, mouseActive);

    // 2. Preset update: reads current, writes next (next already seeded from current)
    this.preset.update(ctx);

    // 2. Swap buffers
    this.currentIsA = !this.currentIsA;

    // 3. Filter post-update (reads newly swapped current)
    if (this.filter) {
      const postCtx = this.buildContext(dt, elapsed, mouseX, mouseY, mouseActive);
      this.filter.postUpdate(postCtx);
    }

    // 4. Compute visuals
    const visCtx = this.buildContext(dt, elapsed, mouseX, mouseY, mouseActive);
    for (let i = 0; i < this.count; i++) {
      let v = this.preset.visual(i, visCtx);
      if (this.filter) {
        v = this.filter.transform(i, v, visCtx);
      }
      this.visuals[i] = v;
    }

    this.frame++;
  }

  /**
   * Get visual output for a triangle (used by render pass).
   */
  getVisual(index: number): VisualOutput {
    return this.visuals[index] || { opacity: 0.5, scale: 1, rotation: 0, color: null };
  }

  getPresetId(): string | null {
    return this.preset?.id ?? null;
  }

  getFilterId(): string | null {
    return this.filter?.id ?? null;
  }
}
